import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react'
import { ReactNode } from 'react'
import DialogHeader, { DialogHeaderProps } from './DialogHeader'
import clsx from 'clsx'
import { Sizes } from 'enums/sizes'
import { getSizeValue } from 'helpers/getSizesValue'
import DialogBody, { DialogBodyProps } from './DialogBody'
import DialogFooter, { DialogFooterProps } from './DialogFooter'
import { useDialog } from 'hooks/useDialog'

const sizes = {
  [Sizes.xs]: 'sm:max-w-xs',
  [Sizes.sm]: 'sm:max-w-sm',
  [Sizes.md]: 'sm:max-w-md',
  [Sizes.lg]: 'sm:max-w-lg',
  [Sizes.xl]: 'sm:max-w-xl',
  [Sizes['2xl']]: 'sm:max-w-2xl',
  [Sizes['3xl']]: 'sm:max-w-3xl',
  [Sizes['4xl']]: 'sm:max-w-4xl',
  [Sizes['5xl']]: 'sm:max-w-5xl'
}

interface DialogProps {
  children: ReactNode
  size: Sizes
  className?: string
}

const GenericDialog: React.FC<DialogProps> & {
  Header: React.FC<DialogHeaderProps>
  Body: React.FC<DialogBodyProps>
  Footer: React.FC<DialogFooterProps>
} = ({ children, size, className }) => {
  const { isOpen, closeDialog, isBlank } = useDialog()
  const sizeValue = getSizeValue(size, sizes)

  return (
    <Dialog open={isOpen} onClose={closeDialog} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 z-50 flex w-screen justify-center overflow-y-auto bg-zinc-950/25 px-2 py-2 transition duration-100 focus:outline-0 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:px-6 sm:py-8 lg:px-8 lg:py-16"
      />
      <div className="fixed z-50 inset-0 w-screen overflow-y-auto pt-6 sm:pt-0">
        <div className="grid min-h-full grid-rows-[1fr_auto] justify-items-center sm:grid-rows-[1fr_auto_3fr] sm:p-4">
          <DialogPanel
            transition
            className={clsx(
              className,
              sizeValue,
              'row-start-2 w-full min-w-0 rounded-t-3xl bg-white overflow-hidden p-[--gutter] shadow-lg ring-1 ring-zinc-950/10 [--gutter:theme(spacing.8)] sm:mb-auto sm:rounded-2xl forced-colors:outline',
              'transition duration-100 will-change-transform data-[closed]:translate-y-12 data-[closed]:opacity-0 data-[enter]:ease-out data-[leave]:ease-in sm:data-[closed]:translate-y-0 sm:data-[closed]:data-[enter]:scale-95'
            )}
          >
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

GenericDialog.Header = DialogHeader
GenericDialog.Body = DialogBody
GenericDialog.Footer = DialogFooter

export default GenericDialog
